import { z } from 'zod';
import { ActingFor } from './acting-for-base.js';
import { PlainDate } from './common.js';
import {
  HeroPreference,
  ManageGroupId,
  ManageMain,
  MotionPreference,
  Pronouns,
  StartPage,
} from './me.js';
import { PageSize, TableId } from './paging.js';
import { SortTableId, TableSort, TableSortMode, WaitingOrder } from './sorting.js';
import {
  NotificationChoice,
  NotificationChoiceChange,
  NotificationEventKey,
} from './notification-preferences.js';

const Ref = z.object({ id: z.string(), name: z.string() });

/** A getting-started step in Waiting on you (the same names as its `step`). */
export const GettingStartedStep = z.enum(['preferences', 'training']);
export type GettingStartedStep = z.infer<typeof GettingStartedStep>;

/** A registry key (packages/domain settings); the server validates it and the value. */
export const SettingKey = z.string().regex(/^[a-z][A-Za-z]*\.[A-Za-z]+$/, 'not a setting key');
/** `system`, an award type, a pool, a sector or a person, depending on the key. */
export const SettingScope = z.string().min(1).max(100);

export const SettingView = z.object({
  key: z.string(),
  scope: z.string(),
  value: z.unknown(),
  effectiveFrom: PlainDate.nullable(),
  /** No one has set it: this is the built-in default. */
  isDefault: z.boolean(),
  /** "Last changed by … on …" (PRD §17). */
  lastChanged: z
    .object({ by: Ref.nullable(), at: z.string(), reason: z.string().nullable() })
    .nullable(),
  canEdit: z.boolean(),
  reasonRequired: z.boolean(),
  effectiveDated: z.boolean(),
  /**
   * The saved row this view shows (null: the default, nothing saved yet), for `WriteSetting.basedOn`.
   * Absent where a view is made from several rows.
   */
  version: z.uuid().nullable().optional(),
});
export type SettingView = z.infer<typeof SettingView>;

/** The setting's History tab (PRD §17): every value it has had, newest first. */
export const SettingHistory = z.object({
  key: z.string(),
  scope: z.string(),
  entries: z.array(
    z.object({
      id: z.uuid(),
      value: z.unknown(),
      effectiveFrom: PlainDate.nullable(),
      recordedAt: z.string(),
      by: Ref.nullable(),
      /** Who was signed in when `by` was acted as in Preview as (APP-122, APP-150); else null. */
      previewedBy: Ref.nullable().default(null),
      reason: z.string().nullable(),
    }),
  ),
});
export type SettingHistory = z.infer<typeof SettingHistory>;

export const WriteSetting = z
  .object({
    key: SettingKey,
    scope: SettingScope,
    value: z.unknown(),
    /** Required for dated settings such as FMV and the split. */
    effectiveFrom: PlainDate.optional(),
    reason: z.string().trim().max(2000).optional(),
    /**
     * The `version` the change was made from (SENT-041). When it's no longer what's saved, someone
     * changed the setting meanwhile, and the write is refused (409 `conflict`) rather than put
     * their change back. Required for a setting written whole, such as `ai.features` (the
     * registry's `versionRequired`, SENT-042); optional for the rest, which a writer may set outright.
     */
    basedOn: z.uuid().nullable().optional(),
    /**
     * APP-148 R73: the same key and value in more scopes, as one change (the minimum options for
     * SDB and EDB): each scope is checked as a write of its own (who may change it, `basedOn`),
     * and all are written in one transaction or none, with one record of what was done for whom.
     */
    alsoScopes: z
      .array(z.object({ scope: SettingScope, basedOn: z.uuid().nullable().optional() }).strict())
      .min(1)
      .max(10)
      .optional(),
    /** APP-148: a Stock Admin changing an Executive's setting for them. */
    actingFor: ActingFor.optional(),
  })
  .strict();
export type WriteSetting = z.infer<typeof WriteSetting>;

/**
 * `GET /api/v1/settings`: the settings this person may read and edit somewhere, from the domain
 * registry (QA-025). Settings shows only the cards these name and never asks for a refused one;
 * scoped reads still check each sector or pool they're asked for.
 */
export const SettingsAccess = z.object({
  readable: z.array(SettingKey),
  editable: z.array(SettingKey),
  /**
   * May open the dedicated template list, which also permits current Spot Committee members to read
   * clean Spot metadata. This grants no generic setting read, history, edit or file access.
   * Optional for compatibility with older responses; current servers always resolve it.
   */
  letterTemplatesReadable: z.boolean().optional(),
});
export type SettingsAccess = z.infer<typeof SettingsAccess>;

/** My settings (SET1): everyone's own preferences. */
export const MySettings = z.object({
  /** The name we greet you by; empty uses your first name. */
  preferredName: z.string(),
  /** Optional pronouns (SET1); null shows none. Optional, so older readers and writers still fit. */
  pronouns: Pronouns.nullable().optional(),
  /** Where sign-in lands (SET1); one of the person's own Level 1 items. */
  startPage: StartPage.optional(),
  /**
   * APP-236 §4: the Manage group the person chose as their main work, or null for Automatic. As
   * saved: a group they no longer have is still here, and Manage ignores it until they have it
   * again (`manageLayoutFor`). Optional, so older readers and writers still fit.
   */
  mainWork: ManageGroupId.nullable().optional(),
  hero: HeroPreference,
  motion: MotionPreference,
  accessibility: z.object({
    contrast: z.enum(['standard', 'high']),
    textSize: z.enum(['standard', 'large']),
  }),
  /**
   * Getting-started steps dismissed from Waiting on you (K25: they count "until done or
   * dismissed"). Removing a step shows it again. Optional, so older readers and writers still fit.
   */
  gettingStarted: z
    .object({ dismissed: z.array(GettingStartedStep) })
    .strict()
    .optional(),
  /**
   * Notification choices (SET1, APP-092), by catalog event: only the events a write names
   * change. A required, always-on or group email can't be switched off (K24); the server
   * refuses it. Optional, so older readers and writers still fit.
   */
  notifications: z
    .record(NotificationEventKey, NotificationChoice)
    .refine((v) => Object.keys(v).length <= 64)
    .optional(),
  /**
   * Rows per page the person chose, by table (APP-135): only the tables a write names change; a
   * table not named uses its own starting size. Optional, so older readers and writers still fit.
   */
  tablePageSizes: z.partialRecord(TableId, PageSize).optional(),
  /**
   * The order Waiting on you opens in on Home (APP-149 D7). Optional, so older readers and writers
   * still fit.
   */
  waitingOrder: WaitingOrder.optional(),
  /**
   * Whether each table opens in its default order or as the person last sorted it (APP-149 D7).
   * Optional, so older readers and writers still fit.
   */
  tableSortMode: TableSortMode.optional(),
  /**
   * The last sort, by table (APP-149), used only with `tableSortMode: 'keep'`: only the tables a
   * write names change. Optional, so older readers and writers still fit.
   */
  tableSorts: z.partialRecord(SortTableId, TableSort).optional(),
});
export type MySettings = z.infer<typeof MySettings>;

/**
 * A write names only what changes. A notification change names only the channels switched
 * (`NotificationChoiceChange`); the server keeps the rest of the choice.
 */
export const UpdateMySettings = MySettings.partial()
  .extend({
    notifications: z
      .record(NotificationEventKey, NotificationChoiceChange)
      .refine((v) => Object.keys(v).length <= 64)
      .optional(),
  })
  .strict();
export type UpdateMySettings = z.infer<typeof UpdateMySettings>;

/** A reviewable snapshot for System Admin assistance (APP-094). */
export const PersonSettings = z.object({
  person: Ref,
  settings: MySettings,
  allowedStartPages: z.array(StartPage),
  /**
   * APP-236: the person's Manage main work, what Automatic means for them and the groups Main
   * work offers; null without Manage. Optional for older readers.
   */
  manageMain: ManageMain.nullable().optional(),
  revision: z.string().min(1),
});
export type PersonSettings = z.infer<typeof PersonSettings>;

export const UpdatePersonSettings = z
  .object({
    // A System Admin doesn't set someone's last sort (APP-149): only their choice of how tables open.
    changes: UpdateMySettings.omit({ tableSorts: true }).refine(
      (value) => Object.keys(value).length > 0,
    ),
    expectedRevision: z.string().min(1).max(128),
    reason: z.string().trim().min(1).max(2000),
  })
  .strict();
export type UpdatePersonSettings = z.infer<typeof UpdatePersonSettings>;
